import { DownloadOutlined, DeleteOutlined, EditOutlined, EllipsisOutlined } from '@ant-design/icons'
import { MenuProps, Dropdown, Button, Space } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { formatAsDate } from '../../../../../config/dayjsConfig'
import { useDeleteConfirmModal } from '../../../../../shared/modals'
import { downloadMenuItems } from '../../../../../utils/tableExport'
import { contextCompanyIdSelector, contextCompanySelector } from '../../../../../redux/context/company/selectors'
import { customReportDateSelector } from '../../../../../redux/context/customReports/selectors'
import { useCalculatedReportContext } from '../CalculatedReportContext'
import { CustomReportSectionsType } from '../../../../../redux/context/customReports/typesSection'
import { useGridExport } from '../grid/useGridExport'
import { showErrorMessage } from '../../../../../utils/message'
import sanitizeFilename from '../../../../../utils/sanitizeFilename'
import { getTitleByLanguage } from '../customReportTable/utils'
import { useDeleteSection } from './useDeleteSection'

interface CustomReportSectionActionsProps {
  section: CustomReportSectionsType
  editModal: (section: CustomReportSectionsType) => void
}

const CustomReportSectionActions: React.FC<CustomReportSectionActionsProps> = ({ section, editModal }) => {
  const { t } = useTranslation()
  const companyId = useSelector(contextCompanyIdSelector)
  const company = useSelector(contextCompanySelector)
  const date = useSelector(customReportDateSelector)
  const { reportId } = useCalculatedReportContext()
  const { exportSingleExcel, exportSingleCSV, isExporting } = useGridExport()
  const deleteConfirmModal = useDeleteConfirmModal()
  const deleteSection = useDeleteSection(companyId, reportId)

  const buildRawFileName = () =>
    [company?.name, getTitleByLanguage(section), formatAsDate(date)].filter(Boolean).join('_')

  const handleExcelExport = async () => {
    try {
      const fileName = sanitizeFilename(buildRawFileName(), 'section', 'xlsx')
      await exportSingleExcel(section.id, fileName)
    } catch (error) {
      showErrorMessage(t('export:excel_failed'))
    }
  }

  const handleCSVExport = async () => {
    try {
      const fileName = sanitizeFilename(buildRawFileName(), 'section', 'csv')
      await exportSingleCSV(section.id, fileName)
    } catch (error) {
      showErrorMessage(t('export:csv_failed'))
    }
  }

  const handleMenuClick = ({ key }: any) => {
    switch (key) {
      case 'edit':
        editModal(section)
        break
      case 'delete':
        deleteConfirmModal({
          content: getTitleByLanguage(section),
          onOk: () => deleteSection(section)
        })
        break
      case 'csv':
        handleCSVExport()
        break
      case 'xlsx':
        handleExcelExport()
        break
      default:
        break
    }
  }

  const sectionMenuItems: MenuProps['items'] = [
    {
      icon: <DownloadOutlined />,
      label: `${t('global:download')}`,
      key: 'download',
      disabled: section.type !== 'table',
      children: [...downloadMenuItems]
    },
    { icon: <DeleteOutlined />, label: t('global:delete'), key: 'delete' }
  ]

  const menuProps = {
    items: sectionMenuItems,
    onClick: handleMenuClick
  }

  return (
    <div className="section-actions">
      <Space>
        <Button data-cy={`section-edit-${section.id}`} onClick={() => editModal(section)}>
          <EditOutlined />
          {t('global:edit')}
        </Button>
        <Dropdown data-cy={`section-actions-${section.id}`} placement="bottomRight" menu={menuProps}>
          <Button icon={<EllipsisOutlined />} loading={isExporting} />
        </Dropdown>
      </Space>
    </div>
  )
}

export default CustomReportSectionActions
